/**
 * @file DealStageMoveDialog.tsx
 * @description Перенос сделки в другую стадию с причиной (phase19.31, зона
 * «Клиенты и сделки», задача 3): сервер не закрывает сделку в «Не
 * состоялась» / «Отменена» без причины (`lost_reason_required`).
 *
 * Два входа — одно окно:
 *  - доска: стадия уже выбрана перетаскиванием (`targetStageId`), в окне —
 *    обязательная причина и необязательный комментарий; «Отмена» оставляет
 *    карточку в прежней колонке;
 *  - карточка сделки «Сменить стадию»: выбор стадии из разрешённых, причина
 *    появляется, только если выбранная стадия её требует.
 * Причина — общий `SelectField` из `DealLostReason`; комментарий уходит
 * серверу (`lostReasonNote` для закрытия, `note` для обычного перехода).
 */

import { useEffect, useMemo, useState } from "react";

import { CrmDialogSurface } from "../CrmDialogSurface";
import { Button } from "../../ui/button";
import { DialogFooter } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";
import { SelectField } from "../../ui/SelectField";
import { Textarea } from "../../ui/textarea";
import {
  DEAL_LOST_REASON_LABEL,
  listStages,
  moveDealToStage,
  type DealDto,
  type DealLostReason,
  type PipelineStageDto,
} from "../../../lib/api-crm";
import { DEAL_LOST_REASONS, LOST_REASON_NOTE_MAX, dealMoveErrorText, moveTargets, needsLostReason } from "../../../lib/crm-deal-move";

interface Props {
  deal: DealDto;
  /** Стадии воронки сделки; нет — окно загрузит их само. */
  stages?: PipelineStageDto[];
  /** Стадия выбрана заранее (перенос на доске) — выбора стадии в окне нет. */
  targetStageId?: string;
  onClose: () => void;
  onMoved: (deal: DealDto) => void;
}

const REASON_OPTIONS = DEAL_LOST_REASONS.map((r) => ({ value: r, label: DEAL_LOST_REASON_LABEL[r] }));

export function DealStageMoveDialog({ deal, stages: given, targetStageId, onClose, onMoved }: Props) {
  const notify = useNotify();
  const [loaded, setLoaded] = useState<PipelineStageDto[] | null>(given ?? null);
  const [stageId, setStageId] = useState(targetStageId ?? "");
  const [reason, setReason] = useState<DealLostReason | "">("");
  const [note, setNote] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (given) return;
    let cancelled = false;
    listStages(deal.pipelineId)
      .then((s) => !cancelled && setLoaded(s))
      .catch(() => !cancelled && setFormError("Не удалось загрузить стадии воронки. Закройте окно и попробуйте ещё раз"));
    return () => {
      cancelled = true;
    };
  }, [given, deal.pipelineId]);

  const targets = useMemo(
    () => moveTargets(loaded ?? [], { id: deal.currentStageId, kind: deal.currentStageKind }),
    [loaded, deal.currentStageId, deal.currentStageKind],
  );
  const target = (loaded ?? []).find((s) => s.id === stageId) ?? null;
  const withReason = target ? needsLostReason(target.kind) : false;
  const locked = Boolean(targetStageId);

  async function submit() {
    if (!target) {
      setFormError("Выберите стадию");
      return;
    }
    if (withReason && !reason) {
      setReasonError("Укажите причину — без неё сделку не закрыть");
      return;
    }
    setSaving(true);
    try {
      const comment = note.trim() || undefined;
      const updated = await moveDealToStage(deal.id, {
        toStageId: target.id,
        ...(withReason ? { lostReason: reason as DealLostReason, lostReasonNote: comment } : { note: comment }),
      });
      notify.success(`Сделка перенесена в «${target.name}»`);
      onMoved(updated);
    } catch (err) {
      setFormError(dealMoveErrorText(err));
    } finally {
      setSaving(false);
    }
  }

  const title = locked && target ? `Перенос в «${target.name}»` : "Сменить стадию";

  return (
    <CrmDialogSurface title={title} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {locked ? (
          <p className="type-body text-on-surface-variant">Сделка «{deal.displayName}» закрывается. Укажите, почему.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="deal-move-stage">Новая стадия</Label>
            <SelectField
              id="deal-move-stage"
              ariaLabel="Новая стадия"
              placeholder={loaded ? (targets.length > 0 ? "Выберите стадию" : "Перенести некуда") : "Загружаем стадии…"}
              value={stageId}
              onChange={(value) => {
                setStageId(value);
                setFormError(null);
              }}
              options={targets.map((s) => ({ value: s.id, label: s.name }))}
              disabled={!loaded || targets.length === 0}
            />
          </div>
        )}

        {withReason ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="deal-move-reason">Причина</Label>
            <SelectField
              id="deal-move-reason"
              ariaLabel="Причина"
              placeholder="Выберите причину"
              value={reason}
              onChange={(value) => {
                setReason(value as DealLostReason);
                setReasonError(null);
              }}
              options={REASON_OPTIONS}
            />
            {reasonError ? <p className="type-caption text-[var(--status-error-fg)]">{reasonError}</p> : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="deal-move-note">Комментарий (необязательно)</Label>
          <Textarea
            id="deal-move-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={LOST_REASON_NOTE_MAX}
            rows={3}
            placeholder={withReason ? "Например: выбрали другой сбор, дорого с доставкой" : "Что изменилось"}
          />
        </div>

        {formError ? (
          <p role="alert" className="type-body text-[var(--status-error-fg)]">
            {formError}
          </p>
        ) : null}

        <DialogFooter className="flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={saving} disabled={!target}>
            Перенести
          </Button>
        </DialogFooter>
      </form>
    </CrmDialogSurface>
  );
}
